import React, { useRef, useState, useCallback } from "react";
import { useAIChat } from "./AIChatContext";
import AIChatMessage from "./AIChatMessage";
import "./AIChatPanel.css";

const AIChatPanel = () => {
  const { isOpen, messages, sendMessage, streaming, panelWidth, setPanelWidth, clearMessages, toggle } = useAIChat();
  const [input, setInput] = useState("");
  const listRef = useRef(null);
  const panelRef = useRef(null);
  const [panelHeight, setPanelHeight] = useState("100%");

  // ── Calculate height to fill viewport ──
  React.useEffect(() => {
    const updateHeight = () => {
      if (panelRef.current) {
        const rect = panelRef.current.getBoundingClientRect();
        const available = window.innerHeight - rect.top;
        if (available > 200) setPanelHeight(`${available}px`);
      }
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    const timer = setTimeout(updateHeight, 300);
    return () => {
      window.removeEventListener("resize", updateHeight);
      clearTimeout(timer);
    };
  }, [isOpen]);

  // ── Auto scroll on new messages ──
  React.useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages]);

  // ── Send ──
  const handleSend = () => {
    if (!input.trim() || streaming) return;
    sendMessage(input.trim());
    setInput("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ── Drag to resize ──
  const startResize = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = panelWidth;

    const onMouseMove = (ev) => {
      const newWidth = startWidth + (startX - ev.clientX);
      setPanelWidth(Math.max(280, Math.min(500, newWidth)));
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }, [panelWidth, setPanelWidth]);

  if (!isOpen) return null;

  return (
    <div className="ai-chat-panel" ref={panelRef} style={{ width: panelWidth, minWidth: panelWidth, height: panelHeight }}>
      {/* Resize handle */}
      <div className="ai-chat-resize-handle" onMouseDown={startResize} />

      {/* Header */}
      <div className="ai-chat-header">
        <span>🤖 AI Assistant</span>
        <div className="ai-chat-header-actions">
          <button className="ai-chat-btn-icon" onClick={clearMessages} title="New Chat">✕</button>
          <button className="ai-chat-btn-icon" onClick={toggle} title="Close Panel">◀</button>
        </div>
      </div>

      {/* Messages */}
      <div className="ai-chat-messages" ref={listRef}>
        {messages.length === 0 && (
          <div className="ai-chat-empty">
            <div style={{ fontSize: "1.5rem", marginBottom: 8 }}>🤖</div>
            <div style={{ fontWeight: 600, color: "#1e293b", marginBottom: 4 }}>AI Assistant</div>
            <div style={{ fontSize: "0.8rem", color: "#94a3b8" }}>
              Ask me about screening tools,<br />
              creating workflows, or<br />
              analyzing results.
            </div>
          </div>
        )}
        {messages.map((msg) => (
          <AIChatMessage key={msg.id} message={msg} />
        ))}
      </div>

      {/* Input */}
      <div className="ai-chat-input-area">
        <textarea
          className="ai-chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask me anything..."
          rows={2}
          disabled={streaming}
        />
        <button
          className="ai-chat-send-btn"
          onClick={handleSend}
          disabled={!input.trim() || streaming}
        >
          {streaming ? "..." : "→"}
        </button>
      </div>
    </div>
  );
};

export default AIChatPanel;
